import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {Application} from 'src/app/shared/models/models';
import {ApplicationService} from 'src/app/application/application.service';
import {NGXLogger} from "ngx-logger";
import {MatPaginator} from "@angular/material/paginator";
import {MatTableDataSource} from "@angular/material/table";
import {MatSelect, MatSelectChange} from "@angular/material/select";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {MatOption} from "@angular/material/core";
import * as moment from "moment";
import {Moment} from "moment";
import * as momentTimezone from 'moment-timezone';
import validator from "validator";
import {AuthService} from "../core/auth/auth.service";
import {Permissions} from "../core/auth/permissions";
import {ConfigApiControllerService, ProductControllerService, BranchControllerService} from "falcon-api";
import {FacadeService} from "../shared/services/facade.service";
import {
  APPLICATION_TYPE_JOINT_ACCOUNT_MAINTENANCE,
  APPLICATION_TYPE_NEW_CLIENT_APPLICATION,
  BRANCH_LIST_KEY
} from "../shared/constants";
import {MatSort} from '@angular/material/sort';
import {Subject, take, takeUntil} from 'rxjs';
import {ReferenceCode} from '../shared/models/reference-code';
import {LoadingDialogService} from '../shared/services/loading-dialog.service';
import {Applicant} from 'leia-api';
import {ApplicationFilterService} from '../core/services/application-filter.service';


export class CreateDateFilter {
  DATE_FORMAT: string = "DD-MM-YYYYTHH:mm:ss";
  DATE_FIELD = "createdTimestamp";

  title: string;
  end?: Moment;
  start?: Moment;
  filterString?: string;

  constructor(title: string, start?: Moment, end?: Moment) {
    this.title = title;
    this.start = start;
    this.end = end;
    this.filterString = "";

    // build filter string
    this.buildFilter();
  }

  buildFilter(): void {
    if (this.start) {
      // Format the start date in UTC (moments are already created in UTC)
      const startString = this.start.format(this.DATE_FORMAT);
      this.filterString = `${this.DATE_FIELD} >: '${startString}'`;

      // only define an end if both start and end are defined
      if (this.end) {
        const endString = this.end.format(this.DATE_FORMAT);
        this.filterString += ` and ${this.DATE_FIELD} <: '${endString}'`;
      }
    }
  }
}

export interface AppFilter {
  field: string;
  fieldDescription: string;
  criteria: string[];
  operator: string;
  operatorDescription: string;
  description: string;
  criteriaDisplayText: string;
}

export interface Filter {
  displayText: string;
  fieldName: string;
  isApplied: boolean;
}

export interface Operator {
  displayText: string;
  value: string;
}

export interface ApplicationStatus {
  displayText: string;
  value: string;
}

export interface Channel {
  displayText: string;
  value: string;
}

export interface Ownership {
  displayText: string;
  value: string;
}

export interface Product {
  displayText: string;
  value: string;
  brandId: number;
}

export interface Branch {
  displayText: string;
  value: number;
  branchCity: string;
}

export interface Brand {
  displayText: string;
  value: number;
  product: ProductInBrand[];
}

export interface ProductInBrand {
  code: string;
  uid: string;
}

export interface RcCode {
  displayText: string;
  value: string;
}

const PRODUCT_KEY = "product";
const STATUS_KEY = "status";
const CREATE_DATE_KEY = "createdDate";
const REFERENCE_CODE_KEY = "rcCode";
const TIME_ZONE_CENTRAL = "America/Chicago";

let dateNextDay = momentTimezone.tz(TIME_ZONE_CENTRAL).add(1, 'day').toDate();


@Component({
  selector: 'applications',
  templateUrl: './applications.component.html',
  styleUrls: ['./applications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplicationsComponent implements OnInit, OnDestroy {
  @ViewChild('criteriaMatSelect') criteriaSelected!: MatSelect;

  private _pagingInitialized = false;
  private _sortingInitialized = false;

  private paginator!: MatPaginator;

  @ViewChild('paginator') set matPaginator(paginator: MatPaginator) {
    this.paginator = paginator;
    this.initPaginator();
  }


  private sort!: MatSort;

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this.sort = sort;
    this.initSort();
  };


  customDateFormGroup = new FormGroup({
    startDate: new FormControl(new Date()),
    endDate: new FormControl(dateNextDay)
  });

  applications: Array<Application> = [];
  sortColumns = new Map<string, string>();
  displayedColumns: string[] = ['fullName', 'sessionId', PRODUCT_KEY, STATUS_KEY, CREATE_DATE_KEY, REFERENCE_CODE_KEY, 'newTab'];
  createDateFilters: CreateDateFilter[] = [];
  searchFilterString: string = "";
  selectedDateFilterString: string = "";
  selectedCustomDateFilterString: string = "";
  customSelectedDateFilterString?: string;
  selectedFilter?: Filter;
  selectedOperator?: Operator;
  appliedFilters: AppFilter[] = [];
  availableFilters: Filter[] = [];
  operators: Operator[] = [];
  dataSource: MatTableDataSource<Application> = new MatTableDataSource<Application>([]);
  totalElements: number = 0;
  defaultPageSize: number = 50;
  canAddFilter: boolean = true;
  canExport: boolean = false;
  canApplyFilter: boolean = true;
  showAddFilterForm: boolean = false;
  activeFilter: string = "";
  showEmptyResultMessage = false;
  applicationStatus: ApplicationStatus[] = [];
  selectedField: string = "";
  selectedDateField: string = "";
  CUSTOM_APPLIED_DATE_KEY: string = "";
  customStartDate: any;
  customEndDate: any;
  products: Product[] = [];
  channels: Channel[] = [];
  ownerships: Ownership[] = [];
  brands: Brand[] = [];
  referenceCodes: RcCode[] = [];
  filteredProducts: Product[] = [];
  branchList: Branch[] = [];
  destroySubject: Subject<void> = new Subject();
  disableSearchButton: boolean = true;

  filterForm = this.formBuilder.group({
    field: ['', [Validators.min(1), Validators.required]],
    operator: ['', [Validators.min(1), Validators.required]],
    criteria: ['', Validators.required]
  });

  dateRange = new FormGroup({
    startDate: new FormControl(),
    endDate: new FormControl()
  });


  searchForm = this.formBuilder.group({
    searchText: [''],
    dateRange: [''],
    searchDateText: [''],
    picker: [''],
    startDate: [''],
    endDate: ['']
  });

  isSearchTextChanged: boolean = false;

  @Input() jointApplicationRequests: boolean = false;

  allowApplicationView: boolean = false;
  allowApplicationSearch: boolean = false;
  allowApplicationCreate: boolean = false;
  fieldName: string = '';
  isOldFilter: boolean = false;


  /**
   * Constructor
   * @param appService Application service instance
   * @param router Router instance
   * @param logger Logger instance
   * @param formBuilder FormBuilder instance
   * @param changeDetector ChangeDetectorRef instance
   * @param authService AuthService instance
   * @param productService ProductControllerService instance
   * @param configApiService ConfigApiControllerService instance
   * @param branchService BranchControllerService instance
   * @param facadeService FacadeService instance
   */
  constructor(private appService: ApplicationService,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private formBuilder: FormBuilder,
              private changeDetector: ChangeDetectorRef,
              private logger: NGXLogger,
              private authService: AuthService,
              private productService: ProductControllerService,
              private configApiService: ConfigApiControllerService,
              private branchService: BranchControllerService,
              private facadeService: FacadeService,
              private applicationFilterService: ApplicationFilterService,
              private loadingDialogService: LoadingDialogService) {

    this.setupFilters();
    this.setupOperators();
    this.setupApplicationStatus();
    this.setupChannels();
    this.setupOwnerships();
    this.setupReferenceCodes();

    this.sortColumns.set(PRODUCT_KEY, "productCode");
    this.sortColumns.set(STATUS_KEY, "status");
    this.sortColumns.set(CREATE_DATE_KEY, "createdTimestamp");

    this.facadeService.setPageTitleContext("Applications");
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
  }

  private setupOperators() {
    this.operators.push({displayText: "is equal to", value: ":"});
    this.operators.push({displayText: "is not equal to", value: "!"});
  }

  private setupApplicationStatus() {
    this.applicationStatus.push({displayText: "Created", value: "Created"});
    this.applicationStatus.push({displayText: "Started", value: "Started"});
    this.applicationStatus.push({displayText: "Abandoned", value: "Abandoned"});
    this.applicationStatus.push({displayText: "Approved", value: "Approved"});
    this.applicationStatus.push({displayText: "Under Review", value: "Under Review"});
    this.applicationStatus.push({displayText: "Rejected", value: "Rejected"});
    this.applicationStatus.push({displayText: "Review Closed", value: "Review Closed"});
    this.applicationStatus.push({displayText: "Expired", value: "Expired"});
    this.applicationStatus.push({displayText: "Error", value: "Error"});
    this.applicationStatus.push({displayText: "Pending", value: "Pending"});

    // sort list
    this.applicationStatus.sort((state1, state2) => (state1.displayText < state2.displayText) ? -1 : 1);
  }

  private setupChannels() {
    this.channels.push({displayText: "Digital Self-Serve (BB8)", value: "1"});
    this.channels.push({displayText: "Banking Center (BB8)", value: "2"});
    this.channels.push({displayText: "FSR/RM Assisted (Coruscant)", value: "3"});
    this.channels.push({displayText: "OLB - Joint Request", value: "4"});
    this.channels.push({displayText: "CSR - Joint Request", value: "5"});
    this.channels.push({displayText: "OLB - Authenticated", value: "6"});
  }

  private setupOwnerships() {
    this.ownerships.push({displayText: "Single", value: "0"});
    this.ownerships.push({displayText: "Joint", value: "1"});
  }

  private setupReferenceCodes() {
    this.referenceCodes.push({
      displayText: "RC200 (Alloy - Under Review)",
      value: ReferenceCode.AlloyUnderReview.toString()
    });
    this.referenceCodes.push({
      displayText: "RC201 (DocV - Under Review)",
      value: ReferenceCode.DocVUnderReview.toString()
    });
    this.referenceCodes.push({
      displayText: "RC210 (Fiserv - Enrollment Failure)",
      value: ReferenceCode.FiservEnrollmentFailure.toString()
    });
    this.referenceCodes.push({
      displayText: "RC211 (Fiserv - Joint Enrollment Failure)",
      value: ReferenceCode.JointAccountFailure.toString()
    });
    this.referenceCodes.push({
      displayText: "RC220 (Online Banking - Enrollment Failure)",
      value: ReferenceCode.OnlineBankingEnrollmentFailure.toString()
    });
    this.referenceCodes.push({
      displayText: "RC230 (Unexpected Enrollment Failure)",
      value: ReferenceCode.UnderReviewFailure.toString()
    });
    this.referenceCodes.push({displayText: "RC300 (Alloy - Denied)", value: ReferenceCode.AlloyDenied.toString()});
    this.referenceCodes.push({displayText: "RC301 (DocV - Denied)", value: ReferenceCode.DocVDenied.toString()});
    this.referenceCodes.push({ displayText: "RC303 (Alloy - Qualifile Decline)", value: ReferenceCode.AlloyQualifileDecline.toString() });
    this.referenceCodes.push({
      displayText: "RC310 (Customer Not in Good Standing)",
      value: ReferenceCode.StatusUFailure.toString()
    });
    this.referenceCodes.push({
      displayText: "RC330 (Eligibility Review Failure)",
      value: ReferenceCode.EligibilityReviewFailure.toString()
    });
    this.referenceCodes.push({
      displayText: "RC340 (Unexpected Failure)",
      value: ReferenceCode.InternalFailure.toString()
    });
    this.referenceCodes.push({
      displayText: "RC350 (Alloy - Unexpected Failure)",
      value: ReferenceCode.AlloyFailure.toString()
    });
    this.referenceCodes.push({
      displayText: "RC351 (DocV - Unexpected Failure)",
      value: ReferenceCode.DocVFailure.toString()
    });
    this.referenceCodes.push({
      displayText: "RC360 (Document Capture - Unexpected Failure)",
      value: ReferenceCode.SocureFailure.toString()
    });
    this.referenceCodes.push({
      displayText: "RC370 (Eligibility Review - OFAC Rejected)",
      value: ReferenceCode.EligibilityFailure.toString()
    });
    this.referenceCodes.push({
      displayText: "RC380 (Application - Invalid SSN / Email Combination)",
      value: ReferenceCode.EMailSSNCombinationInvalid.toString()
    });
    this.referenceCodes.push({
      displayText: "RC390 (Eligibility Review - Applicant Info Mismatch)",
      value: ReferenceCode.ApplicantInfoMismatch.toString()
    });
    this.referenceCodes.push({
      displayText: "RC400 (Eligibility Review - Product Rules Failure)",
      value: ReferenceCode.ProductCheckFailure.toString()
    });
    this.referenceCodes.push({
      displayText: "RC401 (Eligibility Review - Account Rules Failure)",
      value: ReferenceCode.AccountRulesFailure.toString()
    });
    this.referenceCodes.push({
      displayText: "RC402 (Eligibility Review - HYSA Account Rules Failure)",
      value: ReferenceCode.HYSAAccountRulesFailure.toString()
    });
  }

  private setupFilters() {
    // Set up date filters in UTC timezone (as expected by backend)
    const todayStartUTC = momentTimezone.tz(TIME_ZONE_CENTRAL).startOf('day').utc();
    const todayEndUTC = momentTimezone.tz(TIME_ZONE_CENTRAL).endOf('day').utc();
    const yesterdayStartUTC = momentTimezone.tz(TIME_ZONE_CENTRAL).startOf('day').subtract(1, 'days').utc();
    const yesterdayEndUTC = momentTimezone.tz(TIME_ZONE_CENTRAL).endOf('day').subtract(1, 'days').utc();
    const pastWeekStartUTC = momentTimezone.tz(TIME_ZONE_CENTRAL).startOf('day').subtract(1, 'weeks').utc();
    const pastMonthStartUTC = momentTimezone.tz(TIME_ZONE_CENTRAL).startOf('day').subtract(1, 'months').utc();
    const todayEndForRange = momentTimezone.tz(TIME_ZONE_CENTRAL).endOf('day').utc();

    this.createDateFilters = [];
    this.createDateFilters.push(new CreateDateFilter("All Dates"));
    this.createDateFilters.push(new CreateDateFilter("Today", todayStartUTC, todayEndUTC));
    this.createDateFilters.push(new CreateDateFilter("Yesterday", yesterdayStartUTC, yesterdayEndUTC));
    this.createDateFilters.push(new CreateDateFilter("Past Week", pastWeekStartUTC, todayEndForRange));
    this.createDateFilters.push(new CreateDateFilter("Past Month", pastMonthStartUTC, todayEndForRange));
    this.createDateFilters.push(new CreateDateFilter("Custom"));

    this.availableFilters.push({fieldName: "brand", displayText: "Brand", isApplied: false});
    this.availableFilters.push({fieldName: "productCode", displayText: "Product", isApplied: false});
    this.availableFilters.push({fieldName: "status", displayText: "Application Status", isApplied: false});
    this.availableFilters.push({fieldName: "branch", displayText: "Branch", isApplied: false});
    this.availableFilters.push({fieldName: "applicationChannelId", displayText: "Channel", isApplied: false});
    this.availableFilters.push({fieldName: "isJoint", displayText: "Ownership", isApplied: false});
    this.availableFilters.push({fieldName: "rcCode", displayText: "Reference Code", isApplied: false});
  }

  /**
   * Init
   */
  ngOnInit(): void {
    let filterConfig: number;
    if (this.appService.filterConfiguration) {
      filterConfig = this.appService.filterConfiguration;
      this.isOldFilter = filterConfig && filterConfig > 1 ? true : false;
    } else {
      filterConfig = Number(this.activatedRoute?.snapshot?.queryParamMap?.get("filterConfig"));
      this.isOldFilter = filterConfig && filterConfig > 1 ? true : false;
      this.appService.filterConfiguration = filterConfig;
    }

    this.loadBrands();

    if (this.isOldFilter) {
      /* Old Filter code start here */
      this.setupOperators();
      this.setupApplicationStatus();
      this.setupChannels();
      this.setupOwnerships();
      this.setupReferenceCodes();
    }
    this.loadingDialogService.openDialog();
    this.appService.isFilterApplied = false;
    if (this.loadFilters()) {
      this.filterApplications(true);

      if (this.selectedDateField === 'Custom') {
        this.extractDateFromString(this.selectedCustomDateFilterString, true);
      }
    } else {
      this.filterDataUrl();
    }

    if (this.selectedDateField === '') this.selectedDateField = this.createDateFilters[0].title;

    const accessPermissionList = this.authService.getAccessPermissions(this.authService.role.value);
    if (accessPermissionList.includes(Permissions.Type.VIEW_APPLICATION)) {
      this.allowApplicationView = true;
    }
    if (accessPermissionList.includes(Permissions.Type.SEARCH_APPLICATIONS)) {
      this.allowApplicationSearch = true;
    }
    if (accessPermissionList.includes(Permissions.Type.CREATE_APPLICATIONS)) {
      this.allowApplicationCreate = true;
    }

    if (this.isOldFilter) {
      /* Old Filter code start here */
      this.loadProducts();
      this.loadBranchDetails();
    }
    // TODO: Enable export at a later time
    this.canExport = false;
  }

  loadProducts() {
    this.productService.getProducts().subscribe(productList => {
      productList.sort((a: any, b: any) => b?.code > a?.code ? -1 : 1).forEach(product => {
        this.products.push({
          displayText: product.code + ' - ' + product.name,
          value: String(product.code),
          brandId: product.brand?.id!
        });
      });
      this.products.forEach(product => {
        if (this.filteredProducts.find((p) => p.value === product.value) === undefined) {
          this.filteredProducts.push(product)
        }
      });

    });
  }

  loadBranchDetails() {
    this.branchService.getBranches().subscribe(result => {
      this.facadeService.addStorageItem(BRANCH_LIST_KEY, JSON.stringify(result));
      result.sort((a: any, b: any) => b?.branchName > a?.branchName ? -1 : 1).forEach(branch => {
        this.branchList.push({
          displayText: branch.branchName + ' Banking Center',
          value: branch.branchId!,
          branchCity: branch.branchCity!
        })
      });
    });
    this.branchList.push({displayText: 'All', value: -1, branchCity: 'All'})
  }

  loadBrands() {
    this.configApiService.getBrands1().subscribe(brandList => {
      brandList.forEach(brand => {
        let productInBrands: ProductInBrand[] = [];
        brand?.products!.map(function (productDetails) {
          let product: ProductInBrand = {
            code: productDetails.code?.toString()!,
            uid: productDetails.productUID!
          }
          productInBrands.push(product);
        });
        this.brands.push({displayText: brand.name!, value: brand.id!, product: productInBrands});
      })
    });
  }

  // custom filter based on url query parameter
  filterDataUrl() {
    const filterData = this.activatedRoute.snapshot.queryParamMap.get("appliedFilterData");
    const filter = this.activatedRoute.snapshot.queryParamMap.get("filter");

    if (filterData) {
      let appliedFilterData = JSON.parse(filterData);
      if (appliedFilterData.length > 0) {
        this.appliedFilters = appliedFilterData;
      }
    }

    if (filter) {
      this.activeFilter = filter;
      this.appService.search(this.activeFilter).subscribe(page => {
        this.applications = [];

        page.content?.map(applicationLog => {
          let app = Application.mapFromApplicantLog(applicationLog);
          app.createdTimestamp = new Date(app.createdTimestamp! + ' UTC');
          this.applications.push(app);
        });

        this.dataSource.data = this.applications;
        this.loadingDialogService.hideDialog();
        this.totalElements = page.totalElements!;
        this.showEmptyResultMessage = !this.applications.length;
        this.changeDetector.markForCheck();
      });
    } else {
      this.filterApplications(false, this.defaultPageSize, 1, this.sortColumns.get(CREATE_DATE_KEY), "DESC");
    }
  }

  initSort(): void {
    if (this._sortingInitialized) {
      return;
    }

    this.sort.sortChange.pipe(takeUntil(this.destroySubject)).subscribe(() => {
      this.paginator.pageIndex = 0
      this.refreshData();
    });

    this._sortingInitialized = true;
  }

  initPaginator(): void {
    if (this._pagingInitialized) {
      return;
    }

    this.paginator.page.pipe(takeUntil(this.destroySubject)).subscribe(() => {
      this.refreshData();
    })

    this._pagingInitialized = true;
  }

  refreshData(): void {
    this.facadeService.pageNumber = this.paginator.pageIndex + 1;
    this.loadingDialogService.openDialog();

    let {direction, active} = this.sort;

    if (!direction) {
      direction = "desc";
      active = CREATE_DATE_KEY;
    }
    try {
      this.filterApplications(false, this.paginator.pageSize, this.paginator.pageIndex + 1, this.sortColumns.get(active), direction)
    } catch (error) {
      this.filterApplications(true, this.paginator.pageSize, this.paginator.pageIndex + 1, this.sortColumns.get(active), direction)
    }
  }

  loadFilters() {
    let isFilterApply = false;
    let filterData = this.facadeService.filterDataOnboarding;
    if (this.jointApplicationRequests) filterData = this.facadeService.filterDataJointMaintenance;

    if (typeof filterData !== "undefined") {
      this.appliedFilters = filterData;
      isFilterApply = true;
    }
    if (this.facadeService.loadSearchText) {

      //set to enable button if the searchText is not empty
      this.disableSearchButton = false;

      if (this.facadeService.appliedDate) {
        const dateArray = this.facadeService.appliedDate.split("@");
        if (dateArray!) {
          this.selectedDateField = dateArray[0];

          if (this.selectedDateField === 'Custom') {
            this.selectedCustomDateFilterString = dateArray[1];
          } else {
            this.selectedDateFilterString = dateArray[1];
          }

        }
      }

      let searchTextData = {searchText: this.facadeService.getFilterSearchText()};
      this.searchForm.patchValue(searchTextData);
      this.searchFilterString = JSON.parse(this.facadeService.loadSearchText);
      isFilterApply = true;
    }
    return isFilterApply;
  }

  /**
   * Called when user clicks row
   * @param application Row that was clicked.
   */
  onApplicationOpenRequest($event: any, application: Application, opensNewTab: boolean) {
    $event.stopPropagation();

    if (this.allowApplicationView) {
      let route = "/applications/" + application.sessionId;

      if (!opensNewTab) {
        this.router.navigate([route]);
      } else {
        this.router.navigate([]).then((result) => {
          window.open(route, '_blank');
        });
      }
    }
  }

  onAddFilterClicked(event: any) {
    this.canAddFilter = false;
    this.showAddFilterForm = true;
  }

  onCancelFilterClicked(event: MouseEvent) {
    this.showAddFilterForm = false;
    this.canAddFilter = true;
    //set all products (reset)
    this.filteredProducts = this.products;
  }

  applyFilter(event: any) {
    this.appService.isFilterApplied = true;
    this.loadingDialogService.openDialog();
    this.appliedFilters = event;
    this.appService.appliedFilterData = this.appliedFilters;
    this.facadeService.addFilterData(this.appliedFilters, this.jointApplicationRequests);
    this.filterApplications(true);

    this.selectedFilter = undefined;
    this.selectedOperator = undefined;

  }


  filterApplications(rebuildFilters: boolean = false, size?: number, page?: number, sort?: string, sortDirection?: string) {
    //Fix for filter search with default All dates
    if (this.selectedDateFilterString === null || this.selectedDateFilterString === '') this.selectedDateFilterString = this.createDateFilters[0].filterString!;
    if (rebuildFilters)
      this.activeFilter = this.buildFilterString();

    if (this.activeFilter !== '') this.activeFilter = this.activeFilter + ' AND';

    if (this.activeFilter.includes('applicationType')) {
      let applicationType = this.activeFilter.split('applicationType IN');
      applicationType[1] = `(${this.jointApplicationRequests ? APPLICATION_TYPE_JOINT_ACCOUNT_MAINTENANCE : APPLICATION_TYPE_NEW_CLIENT_APPLICATION})`;
      this.activeFilter = applicationType.join('applicationType IN');
    } else {
      if (this.jointApplicationRequests) this.activeFilter = this.activeFilter + ' applicationType IN(' + APPLICATION_TYPE_JOINT_ACCOUNT_MAINTENANCE + ')';

      else this.activeFilter = this.activeFilter + ' applicationType IN(' + APPLICATION_TYPE_NEW_CLIENT_APPLICATION + ')';
    }

    if (this.facadeService.pageNumber > 0) {
      page = this.facadeService.pageNumber;
    }

    this.appService.search(this.activeFilter, size, page, sort, sortDirection).subscribe(page => {
      this.applications = [];

      page.content?.map(applicationLog => {
        let app = Application.mapFromApplicantLog(applicationLog);
        app.createdTimestamp = new Date(app.createdTimestamp! + ' UTC');
        const products = [...app?.products!.reduce((object, product) => {
          const key = product.productCode + '-' + product.productDescription;
          const item = object.get(key) || Object.assign({}, product, {
            productCount: 0
          });
          item.productCount += 1;
          return object.set(key, item);
        }, new Map).values()];

        app.products = products;
        this.applications.push(app);
      });

      this.dataSource.data = this.applications;
      this.loadingDialogService.hideDialog();
      this.totalElements = page.totalElements!;
      this.showEmptyResultMessage = !this.applications.length;
      this.changeDetector.markForCheck();
    });
  }

  buildFilterString(): string {
    // build the filterString based off the search text to start
    let filterString = this.searchFilterString;

    // apply additional filters
    let searchConditionsArray: string[] | undefined = new Array();
    let productStr = '';
    let productCriteriaOperator = "IN(";
    let productCodes = '';
    let productUids = '';
    let ssnStr = '';
    let branchStr = '';
    let miscStr = '';
    let selectCriteria = '';
    let selectCriteriaArray: string[];

    this.appliedFilters.forEach((appliedFilter, index) => {
      const builtFilter = this.applicationFilterService.buildFilterString(appliedFilter);
      if (!!builtFilter) {
        searchConditionsArray?.push(builtFilter);
        return;
      }

      selectCriteria = appliedFilter.criteria.toString();
      selectCriteriaArray = selectCriteria.split(',');
      let criteriaOperator = "";
      if (appliedFilter.operator === "~") criteriaOperator = "%";

      if (appliedFilter.field === 'productCode' || appliedFilter.field === 'brand') {
        //This logic is for extracting all the product codes based on "not equals" criteria for both Product & Brand Filter
        if (appliedFilter.operator === "!") {
          if (appliedFilter.field === 'brand') {
            //Append all the product codes based on brand if there are already some product codes exists
            this.brands.filter(brand => !(selectCriteriaArray.includes(brand?.value!.toString())))
              .forEach(productsInBrand =>
                productsInBrand.product.forEach(product => {
                  productUids += "'" + product.uid + "',"
                })
              );
            productUids = productUids.replace(/^,+/, '').replace(/,+$/, '');
          } else {
            productCodes += ',' + this.filteredProducts.filter(product => !(selectCriteriaArray.includes(product.value))).map(e => e.value).join(',');
            productCodes = productCodes.replace(/^,+/, '').replace(/,+$/, '');
          }
        } else {
          //This logic is to append all the product codes of "equals criteria"
          if (appliedFilter.field === 'productCode') {
            selectCriteria.split(',').forEach(criteria => {
              if (!productCodes.includes(criteria)) {
                productCodes += ',' + selectCriteria;
              } else {
                productCodes = selectCriteria;
              }
            })
            productCodes = productCodes.replace(/^,+/, '').replace(/,+$/, '');
          }
          if (appliedFilter.field === 'brand') {
            this.brands.filter(brand => selectCriteriaArray.includes(brand.value.toString())).forEach(productsInBrand => {
              productsInBrand.product.forEach(product => {
                productUids += "'" + product.uid + "',"
              });
            });
            productUids = productUids.replace(/^,+/, '').replace(/,+$/, '');
          }
        }
      } else if (appliedFilter.field === 'branch') {
        if (appliedFilter.operator === "!") {
          if (selectCriteria === '-1') {
            //Reset to empty string for not equalls ALL criteria
            branchStr = 'NULL';
          } else {

            branchStr += ',' + this.branchList.filter(branch => !selectCriteriaArray.includes(branch?.value!.toString())).map(branch => branch.value);
            branchStr = branchStr.replace(/^,+/, '').replace(/,+$/, '').replace('-1,', ' ');

          }
        } else {
          //Logic to handle "All" filter criteria
          if (selectCriteria === '-1') {
            branchStr += ',' + this.branchList.filter(branch => branch?.value!.toString() !== '-1').map(branch => branch.value);
          } else {
            branchStr += ',' + selectCriteria;
          }
          branchStr = branchStr.replace(/^,+/, '').replace(/,+$/, '').replace('-1,', ' ');
        }
        if (branchStr === 'NULL') {
          branchStr = `branchId IS ${branchStr}`;
        } else {
          branchStr = `branchId IN (${branchStr})`;
        }
        searchConditionsArray?.push(branchStr);
      } else {
        //This else block handles the Application status logic
        let miscQueryStr = '';
        let statusStr = '';
        //This logic is to extract criteria
        if (appliedFilter.operator === "!") {
          if (appliedFilter.field === 'applicationChannelId') {
            statusStr += ',' + this.channels.filter(channel => !selectCriteriaArray.includes(channel?.value!)).map(channel => channel.value).join(',');
          } else if (appliedFilter.field === 'isJoint') {
            statusStr += ',' + this.ownerships.filter(ownership => !selectCriteriaArray.includes(ownership?.value!)).map(ownership => ownership.value).join(',');
          } else {
            statusStr += ',' + this.applicationStatus.filter(status => !selectCriteriaArray.includes(status?.value!)).map(status => status.value).join(',');
          }
          statusStr = statusStr.replace(/^,+/, '').replace(/,+$/, '');
        } else {
          statusStr += ',' + selectCriteria;
          statusStr = statusStr.replace(/^,+/, '').replace(/,+$/, '');
        }
        selectCriteriaArray = statusStr.split(',');
        selectCriteriaArray.forEach((filter) => {
          miscQueryStr += ',' + "'" + filter + "'";
          miscQueryStr = miscQueryStr.replace(/^,+/, '').replace(/,+$/, '');
        });
        miscStr = `${appliedFilter.field} IN(${miscQueryStr})`;
        searchConditionsArray?.push(miscStr);
      }
    });
    //Prepare all the product codes based on product filters
    if (productCodes !== '') {
      productStr = `products.productCode ${productCriteriaOperator}${productCodes})`;
      searchConditionsArray?.push(productStr);
    }
    //Prepare all the product UUIDs based on brand Search filters
    if (productUids !== '') {
      productStr = `products.productUID ${productCriteriaOperator}${productUids})`;
      searchConditionsArray?.push(productStr);
    }
    // Prepare all the Branches info for the final branch query string
    if (branchStr === 'NULL') {
      branchStr = `branchId IS ${branchStr}`;
      searchConditionsArray?.push(branchStr);
    }
    //Prepare final search string for all the search filters
    searchConditionsArray.forEach((searchConditionStr) => {
      if (filterString !== '') {
        filterString += ' and ' + searchConditionStr;
      } else {
        filterString += searchConditionStr;
      }
    });

    // Add date filter as a separate step (not mixed with other filters)
    // This approach avoids the need to remove duplicates
    let dateFilterString = '';
    if (this.selectedDateField === 'Custom') {
      dateFilterString = this.selectedCustomDateFilterString;
    } else {
      dateFilterString = this.selectedDateFilterString;
    }

    // Combine all filters with proper spacing
    if (dateFilterString !== '' && dateFilterString !== null) {
      if (filterString !== '') {
        filterString += ` and ${dateFilterString}`;
      } else {
        filterString = dateFilterString;
      }
    }

    // Store search filter string for next call (without date filters)
    // This ensures we don't accumulate date filters in searchFilterString
    if (this.isSearchTextChanged) {
      this.searchFilterString = this.filteredProducts.length > 0 ? this.searchFilterString : this.searchFilterString;
      this.facadeService.addFilterLoadingSearchText(JSON.stringify(this.searchFilterString));
    }

    return filterString;
  }

  onFilterSelected(event: MatSelectChange) {
    let fieldName = event.value;
    let displayText = (event.source.selected as MatOption).viewValue;

    this.selectedFilter = {fieldName, displayText, isApplied: false}
    this.selectedField = fieldName;
  }

  onOperatorSelect(event: MatSelectChange) {
    let value = event.value;
    let displayText = (event.source.selected as MatOption).viewValue;

    this.selectedOperator = {displayText, value};
  }


  onDateFilterSelected(event: MatSelectChange) {
    this.loadingDialogService.openDialog();
    this.isSearchTextChanged = true;
    this.facadeService.addAppliedDate(event.value);
    const dateArray = event.value.split("@");
    this.selectedDateField = dateArray[0];
    this.selectedDateFilterString = dateArray[1];

    if (this.selectedDateField !== 'Custom') {
      this.filterApplications(true);
    } else {
      this.onCustomDateFilterSelected();
    }
  }


  extractDateFromString(dateFilterString: string, reload: boolean) {
    const dateArray = dateFilterString.split("'");
    let sDate = dateArray[1].split("T")[0];
    let eDate = dateArray[3].split("T")[0];

    this.customStartDate = new Date(sDate);
    this.customEndDate = new Date(eDate);

    let sDay = Number(sDate.split("-")[0]);
    let sMonth = Number(sDate.split("-")[1]) - 1;
    let sYear = Number(sDate.split("-")[2]);
    let eDay = Number(eDate.split("-")[0]);
    let eMonth = Number(eDate.split("-")[1]) - 1;
    let eYear = Number(eDate.split("-")[2]);

    this.customDateFormGroup.patchValue({
      startDate: new Date(sYear, sMonth, sDay),
      endDate: new Date(eYear, eMonth, eDay)
    });
  }

  onDateFilterSelectedForCustomDate(CUSTOM_APPLIED_DATE_KEY: string) {
    this.isSearchTextChanged = true;
    this.facadeService.addAppliedDate(CUSTOM_APPLIED_DATE_KEY);
    const dateArray = CUSTOM_APPLIED_DATE_KEY.split("@");
    this.selectedDateField = dateArray[0];
    this.selectedCustomDateFilterString = dateArray[1];
    this.filterApplications(true);
  }

  onCustomDateFilterSelected() {
    this.loadingDialogService.openDialog();
    if (this.customDateFormGroup.value.startDate === null) {
      this.customStartDate = new Date();
      this.customDateFormGroup.value.startDate = this.customStartDate;
    }

    if (this.customDateFormGroup.value.endDate === null) {
      this.customEndDate = new Date();
      this.customDateFormGroup.value.endDate = this.customEndDate;
    }

    let customSearchStartDate = this.customDateFormGroup.value.startDate;
    let customSearchEndDate = this.customDateFormGroup.value.endDate;
    this.buildCustomFilter(moment(new Date(customSearchStartDate)), moment(new Date(customSearchEndDate)));
    this.CUSTOM_APPLIED_DATE_KEY = 'Custom' + "@" + this.selectedCustomDateFilterString;
    this.selectedDateField = 'Custom';
    this.isSearchTextChanged = true;
    this.onDateFilterSelectedForCustomDate(this.CUSTOM_APPLIED_DATE_KEY);
  }

  addDays(dateTime?: any, count_days = 0) {
    return new Date(new Date(dateTime).setDate(dateTime.getDate() + count_days));
  }

  buildCustomFilter(customStart?: Moment, customEnd?: Moment): void {
    let CUSTOM_DATE_FORMAT: string = "DD-MM-YYYYTHH:00:00";
    let CUSTOM_DATE_FIELD = "createdTimestamp";

    if (!customStart) {
      return;
    }

    //To get proper search result for custom date range, add +6 hours for Standard time, +5 hrs for daylight savings
    const startString = moment(customStart).format("DD-MM-YYYY") + (customStart.isDST() ? "T05:00:00" : "T06:00:00");
    this.selectedCustomDateFilterString = `${CUSTOM_DATE_FIELD} >: '${startString}'`;

    // only define an end if start is defined
    if (!customEnd) {
      return;
    }

    //To get proper search result for custom date range, Minus 6 hours for Standard time, -5 hrs for daylight savings  for endTime range.
    const endString = moment(customEnd).format("DD-MM-YYYY") + (customEnd.isDST() ? "T04:59:59" : "T05:59:59");
    this.selectedCustomDateFilterString += ` and ${CUSTOM_DATE_FIELD} <: '${endString}'`;
  }

  removeFilter(filterToRemove: AppFilter) {
    if (!this.isOldFilter) {
      this.fieldName = filterToRemove.field;
    }
    this.loadingDialogService.openDialog();
    this.appliedFilters = this.appliedFilters.filter(appliedFilter => appliedFilter !== filterToRemove);
    this.appService.appliedFilterData = this.appliedFilters;
    this.facadeService.addFilterData(this.appliedFilters, this.jointApplicationRequests);
    //IF brand, reload the products with all products.
    if (filterToRemove.field === 'brand') {
      this.filteredProducts = this.products;
    }
    this.filterApplications(true);
  }

  clearAllFilters() {

    this.loadingDialogService.openDialog();
    this.appliedFilters = [];
    this.appService.appliedFilterData = this.appliedFilters;
    this.selectedFilter = undefined;
    if (this.isOldFilter) {
      this.filterForm.reset();
    }

    this.facadeService.filterDataJointMaintenance = "";
    this.facadeService.addFilterData(this.appliedFilters, this.jointApplicationRequests);
    //if the searchText remove search text from the service
    let searchText = this.searchForm.get('searchText')?.value.trim();
    if (searchText) {
      this.searchFilterString = "";
      this.facadeService.addFilterSearchText(this.searchFilterString);
      this.facadeService.loadSearchText = "";
      this.searchForm.reset();
    }
    // Clear filter from url query parameters
    this.router.navigate([], {
      queryParams: {
        'filter': null,
        'appliedFilterData': null,
      },
      queryParamsHandling: ''
    })

    this.selectedDateFilterString ? this.selectedDateFilterString = "" : "";
    this.selectedDateField ? this.selectedDateField = "" : "";

    this.filterApplications(true);
    if (!this.isOldFilter) {
      this.appService.isFilterApplied = false;
    }

  }

  /**
   * enable search button if the entered value text >= 2.
   * @param event
   */
  onSearchInputChanges(event: any) {
    if (event.target.value.trim().length >= 2) this.disableSearchButton = false;
    else this.disableSearchButton = true;
  }

  onDownloadResultsClick(event: any, includeLogs: boolean) {
    this.appService.download(this.activeFilter, includeLogs);
  }

  onSearch(event: any) {
    this.loadingDialogService.openDialog();
    this.searchFilterString = "";
    this.appService.isFilterApplied = true;

    let searchText = this.searchForm.get('searchText')?.value.trim();
    //if the searchText is empty, remove search text from the service
    if (this.facadeService.getFilterSearchText() && searchText === "") {
      this.facadeService.addFilterSearchText(searchText);
      this.facadeService.loadSearchText = "";
    }
    // if the searchText is empty, skip the separation of
    if (searchText === "") {
      this.filterApplications(true);
      return;
    }

    //store search text in service
    this.facadeService.addFilterSearchText(searchText);
    // split first space into multiple terms
    let searchParts = searchText.split(/ (.+)/);

    // build the OR condition
    searchParts.forEach((searchPart: string) => {
      if (searchPart !== "") {
        if (validator.isUUID(searchPart)) {
          this.searchFilterString += `(sessionId: '${searchPart}')`;
        } else {
          this.searchFilterString += `(applicants.firstName ~ '%${searchPart}%' OR applicants.lastName ~ '%${searchPart}%')`;
        }

        this.searchFilterString += " OR ";
      }
    });

    // remove the extraneous ' OR '
    this.searchFilterString = this.searchFilterString.trim().replace(new RegExp("[ OR ]+$"), "");

    // format the search string
    this.searchFilterString = `(${this.searchFilterString})`; // close the OR condition
    this.isSearchTextChanged = true;
    this.filterApplications(true);
  }

  /* Old filter function onSaveFilterClicked*/
  onSaveFilterClicked(event: MouseEvent) {
    this.loadingDialogService.openDialog();
    this.appService.isFilterApplied = true;
    if (this.filterForm.invalid || !this.selectedOperator || !this.selectedFilter) {
      this.filterForm.markAllAsTouched();
      this.loadingDialogService.hideDialog();
      return
    }

    let field = this.selectedFilter!.fieldName;
    let fieldDescription = this.selectedFilter!.displayText;

    let operator = this.selectedOperator!.value;
    let operatorDescription = this.selectedOperator!.displayText;

    let criteria = this.filterForm.get('criteria')?.value;
    let criteriaDisplayText = '';
    if (field === 'brand') {
      this.filteredProducts = [];
      this.products.forEach(product => {
        criteria.forEach((val: any) => {
          if (product.brandId === val)
            this.filteredProducts.push(product);
        });
      });
    }

    if (field === 'brand' || field === 'branch' || field === 'applicationChannelId' || field === 'isJoint') {
      criteriaDisplayText = this.criteriaSelected.triggerValue;
    }
    let description = `${fieldDescription} ${operatorDescription} ${criteria}`;

    this.appliedFilters.push({
      field,
      fieldDescription,
      operator,
      operatorDescription,
      criteria,
      description,
      criteriaDisplayText
    });
    this.appService.appliedFilterData = this.appliedFilters;
    this.facadeService.addFilterData(this.appliedFilters, this.jointApplicationRequests);
    this.filterApplications(true);

    this.selectedFilter = undefined;
    this.selectedOperator = undefined;
    this.filterForm.reset();
  }

  getApplicantDisplayName(applicant: Applicant) {
    if (!applicant.firstName && !applicant.lastName) return "Name not provided";

    return [applicant.firstName, applicant.middleName, applicant.lastName, applicant.suffix].join(' ').trim();
  }
}
